




































































import Vue from 'vue';
import { createComponent, Ref, ref, computed } from '@vue/composition-api';

import ContentBlock from '@sbvr/common/legacy-shared/components/ContentBlock.vue';
import UIInput from '@sbvr/common/legacy-shared/components/ui/UIInput.vue';
import UIButton from '@sbvr/common/legacy-shared/components/ui/UIButton.vue';
import BreadcrumbNav from '@sbvr/common/legacy-shared/components/BreadcrumbNav.vue';
import BottomBar from '@sbvr/common/legacy-shared/components/BottomBar.vue';
import WebPanelPage from '@sbvr/web-panel/WebPanelPage.vue';
import useModalService from '@sbvr/web-panel/modal';

import useEditModel from '@/core/hooks/useEditModel';
import { useI18n } from '@/core/i18n';
import { useStore } from '@/core/store';
import { useRouter } from '@/core/routes';
import DeviceGroup from '@/core/store/types/DeviceGroup';

function useDeviceGroup (deviceGroupId: string) {
  const store = useStore();
  const router = useRouter();

  const defaultDeviceGroup: DeviceGroup = {
    title: '',
    description: '',
    deviceCount: 0,
    devices: [],
  };

  function deviceGroupValidFunction (deviceGroup: Ref<DeviceGroup>): boolean {
    return !!deviceGroup.value.title.length;
  }

  const {
    model: deviceGroup,
    modelChanges: deviceGroupChanges,
    editModel: editDeviceGroup,
    modelInStore: deviceGroupInStore,
  } = useEditModel<DeviceGroup>(
    'DeviceGroup',
    deviceGroupId,
    defaultDeviceGroup
  );

  const saving = ref(false);

  const valid = computed(() => {
    return !!deviceGroup.value.title;
  });

  async function storeDeviceGroup () {
    const payload =
      deviceGroupId === 'new'
        ? deviceGroupChanges.value
        : { id: deviceGroupId, ...deviceGroupChanges.value };

    try {
      saving.value = true;
      await store.dispatch('storeDeviceGroup', payload);
      router.push({ name: 'device-index' });
    } finally {
      saving.value = false;
    }
  }

  return {
    deviceGroup,
    editDeviceGroup,
    saving,
    valid,
    storeDeviceGroup,
    deviceGroupInStore,
  };
}

function useBreadcrumbs (
  deviceGroupId: string,
  deviceGroup: Ref<DeviceGroup>
): { breadcrumbItems: any[] } {
  const i18n = useI18n();

  const breadcrumbItems: any[] = [
    { name: i18n.t('Devices'), routerLink: { name: 'device-index' } },
  ];

  if (deviceGroupId !== 'new') {
    breadcrumbItems.push({ name: deviceGroup.value.title });
  } else {
    breadcrumbItems.push({ name: i18n.t('New Group') });
  }

  return {
    breadcrumbItems,
  };
}

function useDeleteModal (root: Vue, deviceCount: number) {
  const router = useRouter();
  const modalService = useModalService(root);
  const i18n = useI18n();

  async function openDeleteConfirm (): Promise<void> {
    const { confirmed } = modalService.confirm({
      icon: deviceCount === 0 ? 'alert-triangle' : 'trash',
      title:
        deviceCount === 0
          ? i18n.t(
            'You can’t delete this group because it still contains devices.'
          )
          : i18n.t('Are you sure you want to delete this group?'),
      message:
        deviceCount === 0
          ? i18n.t('Move the devices to another group first.')
          : '',
      confirmText: deviceCount === 0 ? i18n.t('Got it') : i18n.t('Yes, Delete'),
      cancelText: deviceCount === 0 ? false : i18n.t('Cancel'),
    });
    if (confirmed) {
      if (deviceCount === 0) return;
      router.push({ name: 'device-index' });
    }
  }

  return {
    openDeleteConfirm,
  };
}

export default createComponent({
  props: {
    deviceGroupId: {
      type: String,
      required: true,
    },
  },
  components: {
    ContentBlock,
    UIInput,
    BreadcrumbNav,
    BottomBar,
    WebPanelPage,
    UIButton,
  },
  setup ({ deviceGroupId }, { root }) {
    // TODO: replace with actual org
    const store = useStore();
    const mockOrganization = {
      deviceCount: store.getters['entities/Device/all']().length,
    };

    const deviceGroupAPI = useDeviceGroup(deviceGroupId);

    return {
      ...deviceGroupAPI,
      ...useBreadcrumbs(deviceGroupId, deviceGroupAPI.deviceGroup),
      ...useDeleteModal(root, deviceGroupAPI.deviceGroup.value.deviceCount),
      organization: mockOrganization,
    };
  },
});
